import useSWR from 'swr';

import { findLocaleFromUrlAndCookie } from '@zooplus/commons-lang';

import { getOfferDiscount } from '../utils/membership-offer';

import { usePriceFormatter } from './usePriceFormatter';

import type {
  MembershipOffer,
  MembershipOffersResponse,
  MembershipOfferInfo,
  MembershipOffersInfo,
} from '@repo/types/membership-offer';

/** An offer exclusively tagged PREMIUM. */
const isPremiumOffer = (offer: MembershipOffer) =>
  offer.tags.length === 1 && offer.tags[0] === 'PREMIUM';

/** An offer tagged both SAVINGS_PLAN and PREMIUM. Used as the premium offer when no PREMIUM-only offer exists. */
const isSavingsPlanOffer = (offer: MembershipOffer) =>
  offer.tags.includes('SAVINGS_PLAN') && offer.tags.includes('PREMIUM');

/** An offer tagged both PREMIUM and TRIAL. */
const isTrialOffer = (offer: MembershipOffer) =>
  offer.tags.includes('TRIAL') && offer.tags.includes('PREMIUM');

export function useMembershipOffer(shouldFetch: boolean = true) {
  return useSWR(
    shouldFetch ? '/semiprotected/api/loyalty-management/memberships/offers' : null,
    async (...args) => {
      const response = await fetch(...args, {
        headers: { locale: findLocaleFromUrlAndCookie() || 'en-GB' },
      });

      if (response.status !== 200) {
        return undefined;
      }

      const raw = (await response.json()) as MembershipOffersResponse;

      if (!Array.isArray(raw.offers) || raw.offers.length === 0) {
        return undefined;
      }

      const premiumOffer = raw.offers.find(isPremiumOffer) ?? raw.offers.find(isSavingsPlanOffer);
      const trialOffer = raw.offers.find(isTrialOffer);

      const result: MembershipOffersInfo = { premiumOffer, trialOffer, renewInfo: raw.renewInfo };
      return result;
    },
    {
      errorRetryInterval: 1000,
      errorRetryCount: 3,
      dedupingInterval: 5000,
      revalidateOnFocus: false,
    },
  );
}

/** Fetch the premium offer (or SAVINGS_PLAN fallback) paired with renewInfo.
 * `data` is undefined when no matching offer exists, mirroring the "no offer" use case. */
export function useMembershipPremiumOffer(shouldFetch: boolean = true) {
  const { data, ...rest } = useMembershipOffer(shouldFetch);
  const offerInfo: MembershipOfferInfo | undefined = data?.premiumOffer
    ? { offer: data.premiumOffer, renewInfo: data.renewInfo }
    : undefined;
  return { ...rest, data: offerInfo };
}

/** Fetch the trial offer paired with renewInfo.
 * `data` is undefined when no trial offer exists. */
export function useMembershipTrialOffer(shouldFetch: boolean = true) {
  const { data, ...rest } = useMembershipOffer(shouldFetch);
  const offerInfo: MembershipOfferInfo | undefined = data?.trialOffer
    ? { offer: data.trialOffer, renewInfo: data.renewInfo }
    : undefined;
  return { ...rest, data: offerInfo };
}

/** Given an offer and articles subtotal, return the potential savings, if available,
 * formatted with the offer currency and rounded */
export function useMembershipOfferSavings(offer: MembershipOffer, articlesSubtotal?: number) {
  const percentageDiscountBenefit = getOfferDiscount(offer);
  const currency = offer.price.currency;
  const formatPrice = usePriceFormatter();

  if (percentageDiscountBenefit && articlesSubtotal) {
    const savings = (percentageDiscountBenefit / 100) * articlesSubtotal;
    return {
      savings: formatPrice(savings, currency, true),
      savingsRaw: savings,
    };
  }

  return {};
}
